import type { ApplicationSettingLanguage } from '@lighthouse/core'
import { useAtomAction, useAtomData } from '@lighthouse/shared'
import { clone } from 'rambda'
import React, { useCallback, useEffect, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import styled from 'styled-components'
import { debounce } from 'throttle-debounce'

import { updateAppVersionConfigAtom } from '@/atoms/application/action'
import { applicationSettingAtom } from '@/atoms/application/state'
import { fetchPageListAtom } from '@/atoms/page/action'

import { LanguageList } from './LanguageList'

interface SettingLanguageProps {}

const SCxContainer = styled.form`
    width: 100%;
    font-size: var(--font-size-normal);
`

const SettingLanguage: React.FunctionComponent<SettingLanguageProps> = props => {
    const language = useAtomData(
        applicationSettingAtom,
        useCallback(s => s?.language, [])
    )
    const { run: updateAppVersionConfig } = useAtomAction(updateAppVersionConfigAtom)
    const { run: fetchPageList } = useAtomAction(fetchPageListAtom)

    const methods = useForm<ApplicationSettingLanguage>({
        mode: 'onChange',
        defaultValues: language
    })

    const { watch, control, setValue } = methods

    const handleSubmit = useMemo(
        () =>
            debounce(500, async (value: ApplicationSettingLanguage) => {
                const isSuccess = await updateAppVersionConfig({
                    config: {
                        language: value
                    }
                })
            }),
        [updateAppVersionConfig]
    )

    useEffect(() => {
        const { unsubscribe } = watch((value, prev) => {
            handleSubmit(clone(value) as ApplicationSettingLanguage)
        })
        return unsubscribe
    }, [handleSubmit, watch])

    useEffect(() => {
        return () => {
            fetchPageList()
        }
    }, [fetchPageList])

    return (
        <FormProvider {...methods}>
            <SCxContainer>
                <LanguageList />
            </SCxContainer>
        </FormProvider>
    )
}

export default SettingLanguage
